import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Button,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { DollarSign, IndianRupee, Check, X } from "lucide-react";
import BRAND from "../theme/talktokam";

const PLANS = [
  {
    name: "Free",
    slug: "free",
    price: { usd: "$0", inr: "₹0" },
    period: "/month",
    desc: "Get started with basic chatbot and AI features",
    popular: false,
    limits: [
      { label: "Chatbots", value: "1" },
      { label: "Chats/Month", value: "500" },
      { label: "AI Messages", value: "50" },
      { label: "Live Agents", value: "0" },
      { label: "Languages", value: "1" },
      { label: "PDF Uploads", value: "3" },
      { label: "Website Crawls", value: "3" },
    ],
    features: [
      "AI Chatbot",
      "Lead Generation Bot",
      "Customer Support Bot",
      "Email OTP Verification",
      "Lead Email Notifications",
      "20+ Industry Templates",
      "Auto Trigger",
    ],
    cta: "Get Started",
    href: "/signup",
    color: "#64748b",
  },
  {
    name: "Starter",
    slug: "starter",
    price: { usd: "$29", inr: "₹2,499" },
    period: "/month",
    desc: "For growing teams that need live support and analytics",
    popular: true,
    limits: [
      { label: "Chatbots", value: "3" },
      { label: "Chats/Month", value: "1,000" },
      { label: "AI Messages", value: "100" },
      { label: "Live Agents", value: "1" },
      { label: "Languages", value: "1" },
      { label: "PDF Uploads", value: "15" },
      { label: "Website Crawls", value: "15" },
    ],
    features: [
      "Everything in Free",
      "Live Chat",
      "Canned Responses",
      "Analytics Dashboard",
      "Webhook Integrations",
      "Google Sheets Sync",
      "Hot Lead Notifications",
      "Auto Email Followup",
      "CSV Export",
      "Contacts Dashboard",
      "Conditional Logic",
      "File Upload (Cloudinary)",
    ],
    cta: "Start Free Trial",
    href: "/signup",
    color: "#4F46E5",
  },
  {
    name: "Growth",
    slug: "growth",
    price: { usd: "$79", inr: "₹5,999" },
    period: "/month",
    desc: "For businesses needing AI-powered conversations at scale",
    popular: false,
    limits: [
      { label: "Chatbots", value: "10" },
      { label: "Chats/Month", value: "5,000" },
      { label: "AI Messages", value: "1,000" },
      { label: "Live Agents", value: "3" },
      { label: "Languages", value: "2" },
      { label: "PDF Uploads", value: "50" },
      { label: "Website Crawls", value: "50" },
    ],
    features: [
      "Everything in Starter",
      "Template Message Analytics",
      "CRM (Standard)",
      "Multi-Lingual (2 Languages)",
      "AI Speech",
      "Voice Intent Mapping",
      "WhatsApp Catalog",
      "WhatsApp E-Commerce",
      "2FA Security",
      "Lead Data Masking",
      "Dynamic Questions",
      "Real Time Search",
    ],
    cta: "Start Free Trial",
    href: "/signup",
    color: "#38bdf8",
  },
  {
    name: "Enterprise",
    slug: "enterprise",
    price: { usd: "$199", inr: "₹14,999" },
    period: "/month",
    desc: "For large organizations with custom needs and unlimited scale",
    popular: false,
    limits: [
      { label: "Chatbots", value: "Unlimited" },
      { label: "Chats/Month", value: "Unlimited" },
      { label: "AI Messages", value: "Unlimited" },
      { label: "Live Agents", value: "Unlimited" },
      { label: "Languages", value: "Unlimited" },
      { label: "PDF Uploads", value: "Unlimited" },
      { label: "Website Crawls", value: "Unlimited" },
    ],
    features: [
      "Everything in Growth",
      "Carousel with Buttons",
      "Order Items (E-Commerce)",
      "Appointment Booking",
      "Authenticator / OTP",
      "Custom SLA",
      "Priority Support",
      "Dedicated Account Manager",
    ],
    cta: "Contact Sales",
    href: "/contact",
    color: "#f59e0b",
  },
];

const COMPARE_FEATURES = [
  {
    category: "Chatbot",
    items: [
      { name: "AI Chatbot", f: true, s: true, g: true, e: true },
      { name: "Lead Generation Bot", f: true, s: true, g: true, e: true },
      { name: "Customer Support Bot", f: true, s: true, g: true, e: true },
      { name: "AI Agents (5 Templates)", f: true, s: true, g: true, e: true },
      { name: "20+ Industry Templates", f: true, s: true, g: true, e: true },
      { name: "15+ Question Types", f: true, s: true, g: true, e: true },
      { name: "Auto Trigger Settings", f: true, s: true, g: true, e: true },
      { name: "Conditional Logic", f: false, s: true, g: true, e: true },
      { name: "File Upload (Cloudinary)", f: false, s: true, g: true, e: true },
      { name: "Dynamic Questions", f: false, s: false, g: true, e: true },
      { name: "Real Time Search", f: false, s: false, g: true, e: true },
      { name: "Carousel with Buttons", f: false, s: false, g: false, e: true },
      {
        name: "Order Items (E-Commerce)",
        f: false,
        s: false,
        g: false,
        e: true,
      },
      { name: "Appointment Booking", f: false, s: false, g: false, e: true },
    ],
  },
  {
    category: "Live Support",
    items: [
      { name: "Live Chat", f: false, s: true, g: true, e: true },
      { name: "Canned Responses", f: false, s: true, g: true, e: true },
      { name: "Chat Transfer", f: false, s: true, g: true, e: true },
      { name: "Team Management", f: false, s: true, g: true, e: true },
    ],
  },
  {
    category: "AI & Knowledge",
    items: [
      { name: "AI Bot in Widget", f: true, s: true, g: true, e: true },
      { name: "PDF Knowledge Upload", f: true, s: true, g: true, e: true },
      { name: "Website Crawl (RAG)", f: true, s: true, g: true, e: true },
      { name: "AI Speech", f: false, s: false, g: true, e: true },
      { name: "Voice Intent Mapping", f: false, s: false, g: true, e: true },
      {
        name: "Multi-Lingual (2+ Langs)",
        f: false,
        s: false,
        g: true,
        e: true,
      },
    ],
  },
  {
    category: "Integrations",
    items: [
      { name: "Website Widget Embed", f: true, s: true, g: true, e: true },
      { name: "Domain Whitelist", f: true, s: true, g: true, e: true },
      { name: "Webhook Integrations", f: false, s: true, g: true, e: true },
      { name: "Google Sheets Sync", f: false, s: true, g: true, e: true },
      { name: "CRM (Standard)", f: false, s: false, g: true, e: true },
      { name: "WhatsApp Catalog", f: false, s: false, g: true, e: true },
      { name: "WhatsApp E-Commerce", f: false, s: false, g: true, e: true },
    ],
  },
  {
    category: "Analytics & Data",
    items: [
      { name: "Analytics Dashboard", f: false, s: true, g: true, e: true },
      {
        name: "Template Message Analytics",
        f: false,
        s: false,
        g: true,
        e: true,
      },
      { name: "CSV Export", f: false, s: true, g: true, e: true },
      { name: "Contacts Dashboard", f: false, s: true, g: true, e: true },
      { name: "Lead Email Notifications", f: true, s: true, g: true, e: true },
      { name: "Hot Lead Notifications", f: false, s: true, g: true, e: true },
      { name: "Auto Email Followup", f: false, s: true, g: true, e: true },
    ],
  },
  {
    category: "Security & Compliance",
    items: [
      { name: "Email OTP Verification", f: true, s: true, g: true, e: true },
      { name: "2FA Security", f: false, s: false, g: true, e: true },
      { name: "Lead Data Masking", f: false, s: false, g: true, e: true },
      { name: "Authenticator / OTP", f: false, s: false, g: false, e: true },
      { name: "Custom SLA", f: false, s: false, g: false, e: true },
      { name: "Priority Support", f: false, s: false, g: false, e: true },
    ],
  },
];

const Pricing = () => {
  const navigate = useNavigate();
  const [currency, setCurrency] = useState("usd");

  return (
    <Box sx={{ background: "#050816", minHeight: "100vh" }}>
      <Box
        sx={{
          position: "relative",
          textAlign: "center",
          py: { xs: 10, md: 14 },
          overflow: "hidden",
          background: `radial-gradient(ellipse 800px 600px at 20% 10%, ${BRAND.cyan}33 0%, transparent 60%), radial-gradient(ellipse 700px 500px at 85% 90%, ${BRAND.purple}33 0%, transparent 60%), radial-gradient(ellipse 600px 400px at 50% 50%, ${BRAND.primary}1a 0%, transparent 60%), linear-gradient(180deg, #050816 0%, #0a0e27 50%, #050816 100%)`,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage: `linear-gradient(rgba(56,189,248,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(56,189,248,0.04) 1px, transparent 1px)`,
            backgroundSize: "60px 60px",
            maskImage:
              "radial-gradient(ellipse at center, black 30%, transparent 70%)",
            WebkitMaskImage:
              "radial-gradient(ellipse at center, black 30%, transparent 70%)",
            pointerEvents: "none",
          }}
        />
        <motion.div
          animate={{ y: [0, -40, 0], scale: [1, 1.1, 1] }}
          transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
          style={{
            position: "absolute",
            top: "5%",
            left: "-5%",
            width: 500,
            height: 500,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${BRAND.cyan}40 0%, transparent 70%)`,
            filter: "blur(60px)",
            pointerEvents: "none",
          }}
        />
        <motion.div
          animate={{ y: [0, 40, 0], scale: [1, 1.15, 1] }}
          transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }}
          style={{
            position: "absolute",
            bottom: "-10%",
            right: "-10%",
            width: 600,
            height: 600,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${BRAND.purple}40 0%, transparent 70%)`,
            filter: "blur(80px)",
            pointerEvents: "none",
          }}
        />
        <motion.div
          animate={{ y: [0, -25, 0], x: [0, 20, 0] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
          style={{
            position: "absolute",
            top: "50%",
            right: "30%",
            width: 300,
            height: 300,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${BRAND.primary}26 0%, transparent 70%)`,
            filter: "blur(40px)",
            pointerEvents: "none",
          }}
        />
        <Container maxWidth="md" sx={{ position: "relative", zIndex: 1 }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                fontSize: { xs: "2rem", md: "3rem" },
                background: "linear-gradient(135deg, #38bdf8, #a855f7)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                mb: 2,
              }}
            >
              Simple, Transparent Pricing
            </Typography>
            <Typography sx={{ fontSize: "1.1rem", color: "#94a3b8", mb: 1 }}>
              No credit card required. Free 14-day trial.
            </Typography>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Box
              sx={{
                display: "inline-flex",
                mt: 3,
                p: 0.5,
                borderRadius: 3,
                bgcolor: "rgba(255,255,255,0.06)",
                backdropFilter: "blur(12px)",
                border: "1px solid rgba(255,255,255,0.08)",
              }}
            >
              <ToggleButtonGroup
                value={currency}
                exclusive
                onChange={(_, v) => v && setCurrency(v)}
                sx={{
                  "& .MuiToggleButton-root": {
                    border: 0,
                    borderRadius: "10px !important",
                    px: 3,
                    py: 1,
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.9rem",
                    color: "#94a3b8",
                    "&.Mui-selected": {
                      bgcolor: `${BRAND.cyan}22`,
                      color: BRAND.cyan,
                      "&:hover": { bgcolor: `${BRAND.cyan}30` },
                    },
                    "&:hover": { bgcolor: "rgba(255,255,255,0.06)" },
                  },
                }}
              >
                <ToggleButton value="usd">
                  <DollarSign size={16} style={{ marginRight: 6 }} /> USD
                </ToggleButton>
                <ToggleButton value="inr">
                  <IndianRupee size={16} style={{ marginRight: 6 }} /> INR
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </motion.div>
        </Container>
      </Box>

      <Container sx={{ py: { xs: 6, md: 10 } }}>
        <Grid container spacing={4} justifyContent="center">
          {PLANS.map((plan, i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <Box
                  sx={{
                    p: 3,
                    borderRadius: 4,
                    bgcolor: plan.popular
                      ? "rgba(99,102,241,0.1)"
                      : "rgba(255,255,255,0.04)",
                    backdropFilter: "blur(16px)",
                    border: plan.popular
                      ? "2px solid rgba(99,102,241,0.5)"
                      : "1px solid rgba(255,255,255,0.06)",
                    height: "100%",
                    position: "relative",
                    boxShadow: plan.popular
                      ? `0 12px 48px ${plan.color}22`
                      : "none",
                    transition: "all 0.3s",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      borderColor: plan.popular
                        ? `rgba(99,102,241,0.7)`
                        : `${plan.color}44`,
                      boxShadow: plan.popular
                        ? `0 16px 56px ${plan.color}33`
                        : `0 12px 40px ${plan.color}11`,
                    },
                  }}
                >
                  {plan.popular && (
                    <Chip
                      label="Most Popular"
                      size="small"
                      sx={{
                        position: "absolute",
                        top: -12,
                        left: "50%",
                        transform: "translateX(-50%)",
                        background: "linear-gradient(135deg, #4F46E5, #7C3AED)",
                        color: "#fff",
                        fontWeight: 700,
                        fontSize: "0.75rem",
                      }}
                    />
                  )}

                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: "1.2rem",
                      color: "#fff",
                      mb: 0.5,
                    }}
                  >
                    {plan.name}
                  </Typography>
                  <Typography
                    sx={{
                      color: "#94a3b8",
                      fontSize: "0.85rem",
                      mb: 2,
                      minHeight: 40,
                    }}
                  >
                    {plan.desc}
                  </Typography>

                  <Box sx={{ mb: 2 }}>
                    <Typography
                      component="span"
                      sx={{
                        fontWeight: 800,
                        fontSize: "2.2rem",
                        background: `linear-gradient(135deg, ${plan.color}, ${plan.color}cc)`,
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                      }}
                    >
                      {currency === "usd" ? plan.price.usd : plan.price.inr}
                    </Typography>
                    <Typography
                      component="span"
                      sx={{ color: "#64748b", fontSize: "0.9rem" }}
                    >
                      {plan.period}
                    </Typography>
                  </Box>

                  <Button
                    fullWidth
                    variant="contained"
                    onClick={() => navigate(plan.href)}
                    sx={{
                      borderRadius: "10px",
                      textTransform: "none",
                      fontWeight: 600,
                      py: 1.2,
                      mb: 3,
                      background: `linear-gradient(135deg, ${plan.color}, ${plan.color}dd)`,
                      boxShadow: `0 4px 15px ${plan.color}44`,
                      "&:hover": {
                        boxShadow: `0 6px 25px ${plan.color}66`,
                        filter: "brightness(1.1)",
                      },
                    }}
                  >
                    {plan.cta}
                  </Button>

                  <Box
                    sx={{
                      borderTop: "1px solid rgba(255,255,255,0.06)",
                      pt: 2,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.7rem",
                        fontWeight: 700,
                        color: "#64748b",
                        textTransform: "uppercase",
                        letterSpacing: 1,
                        mb: 1.5,
                      }}
                    >
                      Limits
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 0.5,
                        mb: 2,
                      }}
                    >
                      {plan.limits.map((l, j) => (
                        <Box
                          key={j}
                          sx={{
                            px: 1,
                            py: 0.4,
                            borderRadius: 1,
                            bgcolor: `${plan.color}11`,
                            border: `1px solid ${plan.color}22`,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "0.6rem",
                              color: "#64748b",
                              lineHeight: 1.2,
                            }}
                          >
                            {l.label}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.75rem",
                              fontWeight: 700,
                              color: plan.color,
                              lineHeight: 1.4,
                            }}
                          >
                            {l.value}
                          </Typography>
                        </Box>
                      ))}
                    </Box>

                    <Typography
                      sx={{
                        fontSize: "0.7rem",
                        fontWeight: 700,
                        color: "#64748b",
                        textTransform: "uppercase",
                        letterSpacing: 1,
                        mb: 1.5,
                      }}
                    >
                      Features
                    </Typography>
                    {plan.features.map((f, j) => (
                      <Typography
                        key={j}
                        sx={{
                          fontSize: "0.8rem",
                          color: "#cbd5e1",
                          mb: 0.7,
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <Check
                          size={12}
                          color="#10b981"
                          style={{ flexShrink: 0 }}
                        />{" "}
                        {f}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>

      <Box
        sx={{
          py: { xs: 6, md: 8 },
          background: "linear-gradient(180deg, #0a0e27 0%, #050816 100%)",
        }}
      >
        <Container maxWidth="lg">
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: { xs: "1.3rem", md: "1.5rem" },
              background: "linear-gradient(135deg, #38bdf8, #a855f7)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              textAlign: "center",
              mb: 4,
            }}
          >
            Compare All Features
          </Typography>

          <Box sx={{ overflowX: "auto", pb: 2 }}>
            <Box sx={{ minWidth: 700 }}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "220px repeat(4, 1fr)",
                  gap: 0,
                  mb: 1,
                }}
              >
                <Box />
                {PLANS.map((p) => (
                  <Box key={p.slug} sx={{ textAlign: "center", py: 1 }}>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: "0.85rem",
                        color: p.popular ? BRAND.cyan : "#94a3b8",
                      }}
                    >
                      {p.name}
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: 800,
                        fontSize: "1rem",
                        background: `linear-gradient(135deg, ${p.color}, ${p.color}cc)`,
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                      }}
                    >
                      {currency === "usd" ? p.price.usd : p.price.inr}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {COMPARE_FEATURES.map((cat, ci) => (
                <Box key={ci} sx={{ mb: 2 }}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "220px repeat(4, 1fr)",
                      gap: 0,
                      bgcolor: "rgba(255,255,255,0.03)",
                      borderRadius: 1,
                      px: 2,
                      py: 0.8,
                      mb: 0.5,
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: "0.8rem",
                        color: BRAND.cyan,
                        textTransform: "uppercase",
                        letterSpacing: 1,
                      }}
                    >
                      {cat.category}
                    </Typography>
                  </Box>
                  {cat.items.map((item, ii) => (
                    <Box
                      key={ii}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "220px repeat(4, 1fr)",
                        gap: 0,
                        px: 2,
                        py: 0.6,
                        "&:hover": { bgcolor: "rgba(255,255,255,0.02)" },
                        borderBottom: "1px solid rgba(255,255,255,0.04)",
                      }}
                    >
                      <Typography sx={{ fontSize: "0.8rem", color: "#cbd5e1" }}>
                        {item.name}
                      </Typography>
                      <Box sx={{ display: "flex", justifyContent: "center" }}>
                        {item.f ? (
                          <Check size={16} color="#10b981" />
                        ) : (
                          <X size={16} color="#ef4444" />
                        )}
                      </Box>
                      <Box sx={{ display: "flex", justifyContent: "center" }}>
                        {item.s ? (
                          <Check size={16} color="#10b981" />
                        ) : (
                          <X size={16} color="#ef4444" />
                        )}
                      </Box>
                      <Box sx={{ display: "flex", justifyContent: "center" }}>
                        {item.g ? (
                          <Check size={16} color="#10b981" />
                        ) : (
                          <X size={16} color="#ef4444" />
                        )}
                      </Box>
                      <Box sx={{ display: "flex", justifyContent: "center" }}>
                        {item.e ? (
                          <Check size={16} color="#10b981" />
                        ) : (
                          <X size={16} color="#ef4444" />
                        )}
                      </Box>
                    </Box>
                  ))}
                </Box>
              ))}
            </Box>
          </Box>

          <Box sx={{ textAlign: "center", mt: 4 }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate("/signup")}
              sx={{
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 700,
                px: 4,
                py: 1.5,
                background: "linear-gradient(135deg, #4F46E5, #7C3AED)",
                boxShadow: "0 4px 15px rgba(79,70,229,0.4)",
                "&:hover": { boxShadow: "0 6px 25px rgba(79,70,229,0.6)" },
              }}
            >
              Start Free Trial
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Pricing;
